/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useEffect } from 'react';
import { COOKIE_EXPIRATION_DAYS, COOKIE_URL_TRACKER_ENTRIES_LIMIT, COOKIE_URL_TRACKER_NAME } from '../../constants/cookies';
import type { UrlTrackerEntry } from './types';
import { format, addDays } from 'date-fns';

export function useUrlTracker(): void {
	useEffect( () => {
		const urlTrackerNewEntry: UrlTrackerEntry = {
			url: window.location.pathname,
			dt: format( new Date(), 'yyyy-MM-dd HH:mm:ss' )
		};

		const urlTrackerEntries: Array<UrlTrackerEntry> = [ urlTrackerNewEntry ];

		const urlTrackerCookie = getCookie( COOKIE_URL_TRACKER_NAME );

		if ( urlTrackerCookie ) {
			try {
				const urlTrackerOldEntries: Array<UrlTrackerEntry> = JSON.parse( urlTrackerCookie );

				urlTrackerEntries.push( ...urlTrackerOldEntries );
			} catch ( err ) {
				// If loading old entries fails, we ignore that and only inject the fresh cookie.
			}
		}

		const cookieStringified = JSON.stringify(
			urlTrackerEntries.slice( 0, COOKIE_URL_TRACKER_ENTRIES_LIMIT )
		);

		setCookie( COOKIE_URL_TRACKER_NAME, cookieStringified );
	}, [] );
}

function setCookie( name: string, value: string ): void {
	const expirationDate = addDays( new Date(), COOKIE_EXPIRATION_DAYS );

	document.cookie = [
		`${ name }=${ value }`,
		// @ts-expect-error COOKIE_DOMAIN variable is injected by vite.
		`domain=${ COOKIE_DOMAIN }`,
		`expires=${ expirationDate.toUTCString() }`,
		'path=/'
	].join( '; ' );
}

function getCookie( cookieName: string ): string | null {
	const cookies = decodeURIComponent( document.cookie ).split( /; ?/ );

	for ( const cookie of cookies ) {
		const namePrefix = `${ cookieName }=`;

		if ( cookie.startsWith( namePrefix ) ) {
			return cookie.slice( namePrefix.length );
		}
	}

	return null;
}
